import { declineQuoteInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { useInvalidateMyQuotes } from '@/entities/quote';
import { trpc } from '@/shared/api';
import { getErrorMessage, getTrpcErrorCode } from '@/shared/lib';

export interface DeclineQuoteInput {
  readonly quoteRequestId: string;
  /** 선택 사항(500자 이하) — 앞뒤 공백을 지우고 비어 있으면 null 로 보낸다. */
  readonly reason: string | null;
}

export interface UseDeclineQuoteResult {
  /** 성공하면 true. 확인 단계는 호출부(거절 폼)가 보여 준다. */
  readonly decline: (input: DeclineQuoteInput) => Promise<boolean>;
  readonly isPending: boolean;
  /** 사유 칸 오류(500자 초과). */
  readonly fieldError: string | null;
  readonly error: string | null;
}

const ERROR_FALLBACK = '견적 거절에 실패했습니다.';
const REASON_MESSAGE = '거절 사유는 500자 이하로 입력해 주세요.';

/**
 * 견적 거절(quotes.decline, 견적 설계 §7-7) — 사유는 선택(500자 이하)이고 성공하면 상세·목록을 다시 불러온다.
 * 상태 충돌(CONFLICT, 그 사이 재발행·만료·반려 등)이면 문구와 함께 상세·목록을 다시 불러와 낡은 버튼을 걷어 낸다.
 */
export const useDeclineQuote = (): UseDeclineQuoteResult => {
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invalidate = useInvalidateMyQuotes();
  const mutation = trpc.quotes.decline.useMutation();

  const decline = async (input: DeclineQuoteInput): Promise<boolean> => {
    setFieldError(null);
    setError(null);

    const parsed = declineQuoteInputSchema.safeParse(input);

    if (!parsed.success) {
      setFieldError(REASON_MESSAGE);
      return false;
    }

    try {
      await mutation.mutateAsync(parsed.data);
      await Promise.all([invalidate.detail(input.quoteRequestId), invalidate.list()]);
      return true;
    } catch (caught) {
      setError(getErrorMessage(caught, ERROR_FALLBACK));

      if (getTrpcErrorCode(caught) === 'CONFLICT') {
        await Promise.all([invalidate.detail(input.quoteRequestId), invalidate.list()]);
      }

      return false;
    }
  };

  return { decline, isPending: mutation.isPending, fieldError, error };
};
